// Copyright 2025-2026 Florian Gallon. All Rights reserved.
//
// Bakery address = street (free text "businessAddress") + postcode + town (separate, mandatory fields).
// Older bakeries typed the full address in businessAddress: never add postcode/town twice.

import { isValidRegion } from './regions';

const normalize = (s) => (s || '').toLowerCase().replace(/\s+/g, '');

// Countries writing "postcode town" (most of continental Europe). Everywhere else — US, Canada,
// UK, … — writes "town postcode". Add a country here when it becomes available in the app.
const POSTCODE_FIRST_COUNTRIES = new Set([
  'FR', 'BE', 'CH', 'LU', 'MC', 'DE', 'AT', 'NL', 'ES', 'PT', 'IT',
  'DK', 'SE', 'NO', 'FI', 'PL', 'CZ', 'SK', 'HU', 'RO', 'HR', 'SI', 'GR'
]);

/** True when the country writes "postcode town" (forms show the postcode field first). */
export function isPostcodeFirst(country) {
  return POSTCODE_FIRST_COUNTRIES.has((country || '').toUpperCase());
}

/**
 * Postcode + town line in the local order:
 * "74000 Annecy" (France, most of Europe), "Montréal QC H2X 3K6" (Canada),
 * "Springfield, IL 62704" (US), "London SW1A 2AA" (UK).
 * The province/state (region) is only used for Canada and the US, and only when valid.
 */
export function formatPostcodeCity({ postalCode, city, country, region }) {
  const pc = (postalCode || '').trim();
  const town = (city || '').trim();
  const cc = (country || '').toUpperCase();
  const reg = isValidRegion(cc, region) ? region : '';
  if (!pc && !town) return '';
  if (POSTCODE_FIRST_COUNTRIES.has(cc)) return [pc, town].filter(Boolean).join(' ');
  if (cc === 'US' && reg) return [town ? `${town},` : '', reg, pc].filter(Boolean).join(' ');
  return [town, reg, pc].filter(Boolean).join(' ');
}

/**
 * Full bakery address from its parts.
 * @param {object} parts { businessAddress, postalCode, city, country }
 * @param {object} [opts] { separator: ', ' } — use '\n' for multi-line (labels)
 * @returns {string} '' when nothing is known
 */
export function formatBakeryAddress({ businessAddress, postalCode, city, country, region } = {}, { separator = ', ' } = {}) {
  const street = (businessAddress || '').trim();
  const pc = (postalCode || '').trim();
  const town = (city || '').trim();

  // Old free-text addresses already containing the postcode (or the town when no postcode) stay as typed
  const alreadyComplete = pc
    ? normalize(street).includes(normalize(pc))
    : town && normalize(street).includes(normalize(town));
  if (street && alreadyComplete) return street.replace(/\s*\n\s*/g, separator);

  const line2 = formatPostcodeCity({ postalCode: pc, city: town, country, region });
  return [street.replace(/\s*\n\s*/g, separator), line2].filter(Boolean).join(separator);
}

/**
 * Address parts for a bakery: bakerSettings first, then the bakeries doc
 * (registration writes postcode/town on the bakeries doc only).
 */
export function bakeryAddressParts(bakerSettings = {}, bakery = {}) {
  return {
    businessAddress: bakerSettings?.businessAddress || '',
    postalCode: bakerSettings?.postalCode || bakery?.postalCode || '',
    city: bakerSettings?.city || bakery?.city || '',
    region: bakerSettings?.region || bakery?.region || '',
    country: bakery?.country || bakerSettings?.country || ''
  };
}
